import { useEffect, useState, useRef } from "react";

type Props = {
  /** The full target text. As this prop changes (streaming), characters reveal at a natural pace. */
  text: string;
  /** When true, the cursor blinks and characters keep arriving. When false, no cursor — fully written. */
  isLive: boolean;
  /** Optional className for the wrapper. */
  className?: string;
};

/**
 * Renders text as if being handwritten in real time.
 *
 * Word-atomic word wrapping: each word is a single `inline-block` unit with
 * `white-space: nowrap`, so the browser only breaks between words. Within each
 * word, characters animate in via the ink-stroke fade-rise.
 *
 * Two modes:
 *   1. STREAMING — parent's `text` prop grows as tokens arrive; new chars get the stroke.
 *   2. PRE-FILLED — full text given at once; we reveal it at ~25-35 chars/sec with
 *      longer pauses at punctuation (210-320ms on sentence ends).
 */
export function TypewriterText({ text, isLive, className }: Props) {
  const [revealed, setRevealed] = useState("");
  const [phase, setPhase] = useState<"writing" | "resting" | "done">("done");
  const lastTextRef = useRef("");
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    // Reduced-motion: reveal the whole text at once, no per-character animation.
    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setRevealed(text);
      lastTextRef.current = text;
      setPhase("done");
      return;
    }

    if (text.length < lastTextRef.current.length) {
      setRevealed("");
      lastTextRef.current = "";
    }

    if (isLive) {
      setRevealed(text);
      lastTextRef.current = text;
      setPhase("writing");
      return;
    }

    if (text === revealed) {
      setPhase("done");
      return;
    }

    setPhase("writing");
    let i = revealed.length;
    const step = () => {
      if (i >= text.length) {
        setPhase("resting");
        timerRef.current = window.setTimeout(() => setPhase("done"), 700);
        return;
      }
      const next = text.slice(0, i + 1);
      setRevealed(next);
      lastTextRef.current = next;
      const ch = text[i];
      const delay = pauseFor(ch);
      i += 1;
      timerRef.current = window.setTimeout(step, delay);
    };
    step();

    return () => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, isLive]);

  const showCursor = phase === "writing" || phase === "resting";

  // Split into [word, space, word, space, ...] preserving whitespace as separate
  // tokens so we can render words as atomic units that don't break mid-word.
  const tokens = revealed.split(/(\s+)/).filter((t) => t.length > 0);

  // Stable key per word position so React keeps each <span> mounted and the
  // ink-stroke CSS animation only fires once per character (not on re-render).
  let charIndex = 0;

  return (
    <span className={className}>
      {tokens.map((token, ti) => {
        const isSpace = /^\s+$/.test(token);
        if (isSpace) {
          // Spaces are normal breakable whitespace — let the browser wrap here.
          charIndex += token.length;
          return (
            <span key={`s-${ti}`} aria-hidden="true">
              {token}
            </span>
          );
        }
        const startIdx = charIndex;
        charIndex += token.length;
        return (
          <span
            key={`w-${ti}-${startIdx}`}
            className="inline-block whitespace-nowrap"
          >
            {token.split("").map((c, ci) => (
              <span key={ci} className="ink-stroke inline-block">
                {c}
              </span>
            ))}
          </span>
        );
      })}
      {showCursor && <span className="ink-cursor" aria-hidden="true" />}
    </span>
  );
}

function pauseFor(ch: string): number {
  if (ch === "." || ch === "?" || ch === "!") return 240 + Math.random() * 80;
  if (ch === ",") return 110 + Math.random() * 40;
  if (ch === ";" || ch === ":") return 140 + Math.random() * 40;
  if (ch === " ") return 28 + Math.random() * 22;
  return 28 + Math.random() * 14;
}
